// src/admin/BusinessManager.js - Fixed - No duplicate header
import React, { useState, useEffect } from 'react';
import {
  Edit,
  Trash2,
  Search,
  Plus,
  Eye,
  Settings,
  Shield,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const BusinessManager = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [newBusiness, setNewBusiness] = useState({
    id: '',
    companyName: '',
    ownerName: '',
    email: '',
    phone: '',
    plan: 'Starter',
    status: 'Active',
    portalCount: 1,
    createdDate: new Date().toLocaleDateString(),
  });

  // Fetch businesses from API
  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      // In a real app, this would be an API call
      // const response = await fetch('/.netlify/functions/get-businesses');
      // const data = await response.json();
      // setBusinesses(data);

      // For demo purposes, we'll use mock data
      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate API delay

      const mockData = [
        {
          id: 'bus-001',
          companyName: 'Acme Tax Services',
          ownerName: 'John Richards',
          email: 'john@acmetax.com',
          phone: '(555) 123-4567',
          plan: 'Professional',
          status: 'Active',
          portalCount: 3,
          createdDate: '1/15/2025',
          lastLogin: '5/15/2025',
          notes: 'Premium customer with multiple offices',
        },
        {
          id: 'bus-002',
          companyName: 'Smith & Associates',
          ownerName: 'Sarah Smith',
          email: 'sarah@smithcpa.com',
          phone: '(555) 234-5678',
          plan: 'Business',
          status: 'Active',
          portalCount: 8,
          createdDate: '2/10/2025',
          lastLogin: '5/14/2025',
          notes: 'Recently upgraded to Business plan',
        },
        {
          id: 'bus-003',
          companyName: 'Davis Financial',
          ownerName: 'Michael Davis',
          email: 'mdavis@davisfinancial.com',
          phone: '(555) 345-6789',
          plan: 'Starter',
          status: 'Trial',
          portalCount: 1,
          createdDate: '5/01/2025',
          lastLogin: '5/10/2025',
          notes: 'Free trial expires in 7 days',
        },
        {
          id: 'bus-004',
          companyName: 'Johnson Tax Prep',
          ownerName: 'Robert Johnson',
          email: 'robert@johnsontax.com',
          phone: '(555) 456-7890',
          plan: 'Professional',
          status: 'Suspended',
          portalCount: 2,
          createdDate: '3/15/2025',
          lastLogin: '4/28/2025',
          notes: 'Payment issue - reached out via email',
        },
        {
          id: 'bus-005',
          companyName: 'Wilson Accounting',
          ownerName: 'Jennifer Wilson',
          email: 'jennifer@wilsonaccounting.com',
          phone: '(555) 567-8901',
          plan: 'Business',
          status: 'Active',
          portalCount: 15,
          createdDate: '11/5/2024',
          lastLogin: '5/15/2025',
          notes: 'Largest client by portal count',
        },
        {
          id: 'bus-006',
          companyName: 'Lee Consulting',
          ownerName: 'Amanda Lee',
          email: 'amanda@leeconsulting.com',
          phone: '(555) 678-9012',
          plan: 'Starter',
          status: 'Inactive',
          portalCount: 1,
          createdDate: '1/20/2025',
          lastLogin: '3/15/2025',
          notes: 'Has not logged in for 60+ days',
        },
      ];

      setBusinesses(mockData);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, []);

  // Filter businesses based on search term and status filter
  const filteredBusinesses = businesses.filter((business) => {
    const matchesSearch =
      business.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      business.ownerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      business.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'All' || business.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Add new business
  const addBusiness = () => {
    // Generate a random ID for the new business
    const newId = `bus-${Math.floor(Math.random() * 1000)}`;
    const businessWithId = { ...newBusiness, id: newId };

    // In a real app, this would be an API call
    // await fetch('/.netlify/functions/add-business', {
    //   method: 'POST',
    //   body: JSON.stringify(businessWithId)
    // });

    setBusinesses([...businesses, businessWithId]);
    setNewBusiness({
      id: '',
      companyName: '',
      ownerName: '',
      email: '',
      phone: '',
      plan: 'Starter',
      status: 'Active',
      portalCount: 1,
      createdDate: new Date().toLocaleDateString(),
    });
    setIsAddModalOpen(false);
  };

  // Update business
  const updateBusiness = () => {
    // In a real app, this would be an API call
    // await fetch(`/.netlify/functions/update-business/${selectedBusiness.id}`, {
    //   method: 'PUT',
    //   body: JSON.stringify(selectedBusiness)
    // });

    const updatedBusinesses = businesses.map((business) =>
      business.id === selectedBusiness.id ? selectedBusiness : business
    );

    setBusinesses(updatedBusinesses);
    setIsEditModalOpen(false);
  };

  // Delete business
  const deleteBusiness = (id) => {
    if (
      window.confirm(
        'Are you sure you want to delete this business? This cannot be undone.'
      )
    ) {
      // In a real app, this would be an API call
      // await fetch(`/.netlify/functions/delete-business/${id}`, {
      //   method: 'DELETE'
      // });

      const updatedBusinesses = businesses.filter(
        (business) => business.id !== id
      );
      setBusinesses(updatedBusinesses);
    }
  };

  // Business Action Buttons Component
  const BusinessActions = ({ business }) => (
    <div className="flex space-x-2">
      <button
        className="p-1 text-blue-600 hover:text-blue-800"
        onClick={() => {
          setSelectedBusiness(business);
          setIsViewModalOpen(true);
        }}
        title="View Details"
      >
        <Eye className="h-5 w-5" />
      </button>
      <button
        className="p-1 text-green-600 hover:text-green-800"
        onClick={() => {
          setSelectedBusiness(business);
          setIsEditModalOpen(true);
        }}
        title="Edit Business"
      >
        <Edit className="h-5 w-5" />
      </button>
      <button
        className="p-1 text-red-600 hover:text-red-800"
        onClick={() => deleteBusiness(business.id)}
        title="Delete Business"
      >
        <Trash2 className="h-5 w-5" />
      </button>
    </div>
  );

  // Status Badge Component
  const StatusBadge = ({ status }) => {
    let badgeClass = '';

    switch (status) {
      case 'Active':
        badgeClass = 'bg-green-100 text-green-800';
        break;
      case 'Trial':
        badgeClass = 'bg-blue-100 text-blue-800';
        break;
      case 'Suspended':
        badgeClass = 'bg-red-100 text-red-800';
        break;
      case 'Inactive':
        badgeClass = 'bg-gray-100 text-gray-800';
        break;
      default:
        badgeClass = 'bg-gray-100 text-gray-800';
    }

    return (
      <span
        className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}
      >
        {status}
      </span>
    );
  };

  // Plan Badge Component
  const PlanBadge = ({ plan }) => {
    let badgeClass = '';

    switch (plan) {
      case 'Starter':
        badgeClass = 'bg-gray-100 text-gray-800 border-gray-300';
        break;
      case 'Professional':
        badgeClass = 'bg-blue-100 text-blue-800 border-blue-300';
        break;
      case 'Business':
        badgeClass = 'bg-purple-100 text-purple-800 border-purple-300';
        break;
      case 'Enterprise':
        badgeClass = 'bg-indigo-100 text-indigo-800 border-indigo-300';
        break;
      default:
        badgeClass = 'bg-gray-100 text-gray-800 border-gray-300';
    }

    return (
      <span
        className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${badgeClass}`}
      >
        {plan}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Business Management
        </h1>

        {/* Search and Filters */}
        <div className="bg-white p-4 rounded-lg shadow border mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search businesses..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex space-x-2">
              <select
                className="p-2 border border-gray-300 rounded-lg"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option>All</option>
                <option>Active</option>
                <option>Trial</option>
                <option>Suspended</option>
                <option>Inactive</option>
              </select>
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
                onClick={() => setIsAddModalOpen(true)}
              >
                <Plus className="h-5 w-5 mr-1" />
                Add Business
              </button>
            </div>
          </div>
        </div>

        {/* Businesses Table */}
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading businesses...</p>
            </div>
          ) : filteredBusinesses.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-600">
                No businesses found matching your criteria.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Portals
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBusinesses.map((business) => (
                    <tr key={business.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {business.companyName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {business.ownerName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {business.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <PlanBadge plan={business.plan} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={business.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {business.portalCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {business.createdDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <BusinessActions business={business} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Business Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Add New Business
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={newBusiness.companyName}
                  onChange={(e) =>
                    setNewBusiness({
                      ...newBusiness,
                      companyName: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Owner Name
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={newBusiness.ownerName}
                  onChange={(e) =>
                    setNewBusiness({
                      ...newBusiness,
                      ownerName: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={newBusiness.email}
                  onChange={(e) =>
                    setNewBusiness({ ...newBusiness, email: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={newBusiness.phone}
                  onChange={(e) =>
                    setNewBusiness({ ...newBusiness, phone: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={newBusiness.plan}
                  onChange={(e) =>
                    setNewBusiness({ ...newBusiness, plan: e.target.value })
                  }
                >
                  <option>Starter</option>
                  <option>Professional</option>
                  <option>Business</option>
                  <option>Enterprise</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={newBusiness.status}
                  onChange={(e) =>
                    setNewBusiness({ ...newBusiness, status: e.target.value })
                  }
                >
                  <option>Active</option>
                  <option>Trial</option>
                  <option>Suspended</option>
                  <option>Inactive</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() => setIsAddModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                onClick={addBusiness}
              >
                Add Business
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Business Modal */}
      {isEditModalOpen && selectedBusiness && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Edit Business
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={selectedBusiness.companyName}
                  onChange={(e) =>
                    setSelectedBusiness({
                      ...selectedBusiness,
                      companyName: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Owner Name
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={selectedBusiness.ownerName}
                  onChange={(e) =>
                    setSelectedBusiness({
                      ...selectedBusiness,
                      ownerName: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={selectedBusiness.email}
                  onChange={(e) =>
                    setSelectedBusiness({
                      ...selectedBusiness,
                      email: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={selectedBusiness.phone}
                  onChange={(e) =>
                    setSelectedBusiness({
                      ...selectedBusiness,
                      phone: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={selectedBusiness.plan}
                  onChange={(e) =>
                    setSelectedBusiness({
                      ...selectedBusiness,
                      plan: e.target.value,
                    })
                  }
                >
                  <option>Starter</option>
                  <option>Professional</option>
                  <option>Business</option>
                  <option>Enterprise</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={selectedBusiness.status}
                  onChange={(e) =>
                    setSelectedBusiness({
                      ...selectedBusiness,
                      status: e.target.value,
                    })
                  }
                >
                  <option>Active</option>
                  <option>Trial</option>
                  <option>Suspended</option>
                  <option>Inactive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Portal Count
                </label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={selectedBusiness.portalCount}
                  onChange={(e) =>
                    setSelectedBusiness({
                      ...selectedBusiness,
                      portalCount: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={selectedBusiness.notes || ''}
                  onChange={(e) =>
                    setSelectedBusiness({
                      ...selectedBusiness,
                      notes: e.target.value,
                    })
                  }
                  rows={3}
                ></textarea>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                onClick={updateBusiness}
              >
                Update Business
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Business Modal */}
      {isViewModalOpen && selectedBusiness && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedBusiness.companyName}
                </h2>
                <p className="text-gray-500 mt-1">{selectedBusiness.id}</p>
              </div>
              <StatusBadge status={selectedBusiness.status} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Business Details
                </h3>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Owner</p>
                    <p className="font-medium">{selectedBusiness.ownerName}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{selectedBusiness.email}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{selectedBusiness.phone}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Created</p>
                    <p className="font-medium">
                      {selectedBusiness.createdDate}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Last Login</p>
                    <p className="font-medium">
                      {selectedBusiness.lastLogin || 'Never'}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Subscription
                </h3>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Plan</p>
                    <p className="font-medium">
                      <PlanBadge plan={selectedBusiness.plan} />
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Portals</p>
                    <p className="font-medium">
                      {selectedBusiness.portalCount}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Notes</p>
                    <p className="font-medium">
                      {selectedBusiness.notes || 'No notes'}
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Actions
                  </h3>
                  <div className="space-y-2">
                    <button className="flex items-center text-indigo-600 hover:text-indigo-800">
                      <Shield className="h-5 w-5 mr-2" />
                      Login as this business
                    </button>
                    <button className="flex items-center text-green-600 hover:text-green-800">
                      <Settings className="h-5 w-5 mr-2" />
                      Manage portals
                    </button>
                    <div className="flex space-x-3 mt-4">
                      {selectedBusiness.status !== 'Active' ? (
                        <button className="flex items-center px-3 py-1 border border-green-500 rounded-md text-green-600 hover:bg-green-50">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Activate
                        </button>
                      ) : (
                        <button className="flex items-center px-3 py-1 border border-red-500 rounded-md text-red-600 hover:bg-red-50">
                          <XCircle className="h-4 w-4 mr-1" />
                          Suspend
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 border-t pt-6 flex justify-end">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() => setIsViewModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessManager;